import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Sparkles, Heart, Gift, Calendar, MessageCircle, ArrowRight, Download } from 'lucide-react';

const IOS_LINK = 'https://apps.apple.com/us/app/couplein/id6749887412';
const ANDROID_LINK = 'https://play.google.com/store/apps/details?id=dev.expo.couplein&pcampaignid=web_share';
const LOGO_URL = '/couplein_logo.png';

const FEATURE_PATHS = [
  '/never-forget-anything',
  '/solve-fights-in-5-minutes',
  '/love-with-intentionality',
  '/stay-connected-in-60-seconds',
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [quizDropdown, setQuizDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('features');
  const dropdownTimeoutRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const path = location.pathname;
  const isHome = path === '/';
  const isBlog = path.startsWith('/blog');
  const isQuiz = path.startsWith('/quiz');
  const isFeature = FEATURE_PATHS.includes(path);

  // Smooth hover handlers for dropdown with grace timeout
  const handleDropdownEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setQuizDropdown(true);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setQuizDropdown(false);
    }, 220); // 220ms grace buffer prevents accidental closes
  };

  // Robust BoundingRect ScrollSpy for Home Sections
  const handleScrollSpy = useCallback(() => {
    const root = document.getElementById('root');
    const currentScroll = root ? root.scrollTop : window.scrollY;
    setScrolled(currentScroll > 20);

    if (!isHome) return;

    // Ordered top-down sections
    const sections = [
      { id: 'faq', name: 'faq' },
      { id: 'testimonials', name: 'testimonials' },
      { id: 'quizzes', name: 'quizzes' },
      { id: 'comparison', name: 'how-it-works' },
      { id: 'how-it-works', name: 'how-it-works' },
      { id: 'screenshots', name: 'features' },
      { id: 'features', name: 'features' },
    ];

    const navThreshold = 140;

    for (const sec of sections) {
      const el = document.getElementById(sec.id);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= navThreshold && rect.bottom > navThreshold) {
          setActiveSection(sec.name);
          return;
        }
      }
    }

    if (currentScroll < 400) {
      setActiveSection('features');
    }
  }, [isHome]);

  useEffect(() => {
    if (!isHome) return;

    const root = document.getElementById('root');
    if (root) root.addEventListener('scroll', handleScrollSpy, { passive: true });
    window.addEventListener('scroll', handleScrollSpy, { passive: true });
    window.addEventListener('resize', handleScrollSpy, { passive: true });

    handleScrollSpy();

    return () => {
      if (root) root.removeEventListener('scroll', handleScrollSpy);
      window.removeEventListener('scroll', handleScrollSpy);
      window.removeEventListener('resize', handleScrollSpy);
    };
  }, [isHome, handleScrollSpy]);

  // Sync active section if hash changes
  useEffect(() => {
    if (isHome && location.hash) {
      const target = location.hash.replace('#', '');
      if (target === 'how-it-works' || target === 'comparison') {
        setActiveSection('how-it-works');
      } else if (target === 'testimonials') {
        setActiveSection('testimonials');
      } else if (target === 'faq') {
        setActiveSection('faq');
      } else if (target === 'features' || target === 'screenshots') {
        setActiveSection('features');
      }
    }
  }, [isHome, location.hash]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setQuizDropdown(false);
  }, [location.pathname]);

  const handleAnchorClick = (e, targetId) => {
    e.preventDefault();
    setIsOpen(false);
    setQuizDropdown(false);
    setActiveSection(targetId === 'comparison' ? 'how-it-works' : targetId);

    if (isHome) {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.pushState(null, '', `/#${targetId}`);
      }
    } else {
      navigate(`/#${targetId}`);
    }
  };

  const getLinkClasses = (isActive) =>
    `transition-all duration-200 py-1.5 px-3 rounded-xl text-sm font-medium ${
      isActive
        ? 'text-pink-700 bg-pink-50/90 font-semibold shadow-xs'
        : 'text-gray-700 hover:text-pink-700 hover:bg-gray-50/80'
    }`;

  const getMobileLinkClasses = (isActive) =>
    `block px-3.5 py-2.5 rounded-xl transition-all font-medium text-base ${
      isActive
        ? 'text-pink-700 bg-pink-50/90 font-semibold'
        : 'text-gray-800 hover:bg-gray-50 hover:text-pink-700'
    }`;

  const isFeaturesActive = isHome ? (!isQuiz && !isBlog && !isFeature && activeSection === 'features') : isFeature;
  const isHowItWorksActive = isHome && activeSection === 'how-it-works';
  const isReviewsActive = isHome && activeSection === 'testimonials';
  const isFaqActive = isHome && activeSection === 'faq';
  const isQuizzesActive = isQuiz || (isHome && activeSection === 'quizzes');

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm'
          : 'bg-white/85 backdrop-blur-md border-b border-gray-100/70'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* Brand Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0" data-testid="nav-logo-link">
            <img
              src={LOGO_URL}
              alt="CoupleIn Logo"
              width="191"
              height="64"
              className="h-12 sm:h-14 md:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center gap-2 xl:gap-3">
            <a
              href="/#features"
              onClick={(e) => handleAnchorClick(e, 'features')}
              className={getLinkClasses(isFeaturesActive)}
            >
              Features
            </a>

            <a
              href="/#how-it-works"
              onClick={(e) => handleAnchorClick(e, 'how-it-works')}
              className={getLinkClasses(isHowItWorksActive)}
            >
              How It Works
            </a>

            {/* Free Quizzes Dropdown with Invisible Bridge & Grace Timeout */}
            <div
              className="relative py-2"
              onMouseEnter={handleDropdownEnter}
              onMouseLeave={handleDropdownLeave}
            >
              <button
                type="button"
                onClick={() => setQuizDropdown((prev) => !prev)}
                className={`flex items-center gap-1 focus:outline-none ${getLinkClasses(isQuizzesActive)}`}
                aria-expanded={quizDropdown}
                data-testid="nav-quizzes-dropdown"
              >
                <span>Free Quizzes</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${quizDropdown ? 'rotate-180 text-pink-600' : ''}`} />
              </button>

              {quizDropdown && (
                <div
                  className="absolute top-full left-0 pt-2 w-72 z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                  onMouseEnter={handleDropdownEnter}
                  onMouseLeave={handleDropdownLeave}
                >
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-2 py-3 ring-1 ring-black/5">
                    <Link
                      to="/quiz/love-language"
                      onClick={() => setQuizDropdown(false)}
                      data-testid="nav-love-language-quiz"
                      className={`flex items-start gap-3 p-2.5 rounded-xl transition-colors group ${
                        path === '/quiz/love-language' ? 'bg-pink-50' : 'hover:bg-pink-50/70'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-pink-100 text-pink-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Heart className="w-4 h-4 fill-current" />
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${path === '/quiz/love-language' ? 'text-pink-600' : 'text-gray-900 group-hover:text-pink-600'}`}>
                          Love Language Quiz
                        </p>
                        <p className="text-xs text-gray-500">Discover what makes each other tick</p>
                      </div>
                    </Link>

                    <Link
                      to="/quiz/attachment-style"
                      onClick={() => setQuizDropdown(false)}
                      data-testid="nav-attachment-quiz"
                      className={`flex items-start gap-3 p-2.5 rounded-xl transition-colors group mt-1 ${
                        path === '/quiz/attachment-style' ? 'bg-purple-50' : 'hover:bg-purple-50/70'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${path === '/quiz/attachment-style' ? 'text-purple-600' : 'text-gray-900 group-hover:text-purple-600'}`}>
                          Attachment Style Quiz
                        </p>
                        <p className="text-xs text-gray-500">Understand your relationship bond</p>
                      </div>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <a
              href="/#testimonials"
              onClick={(e) => handleAnchorClick(e, 'testimonials')}
              className={getLinkClasses(isReviewsActive)}
            >
              Reviews
            </a>

            <a
              href="/#faq"
              onClick={(e) => handleAnchorClick(e, 'faq')}
              className={getLinkClasses(isFaqActive)}
            >
              FAQ
            </a>

            <Link
              to="/blog"
              data-testid="nav-blog-link"
              className={getLinkClasses(isBlog)}
            >
              Blog
            </Link>
          </nav>

          {/* Desktop Right CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={IOS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 bg-black text-white hover:bg-gray-800 text-xs font-semibold px-4 py-2.5 rounded-xl transition-all hover:scale-105 active:scale-95 shadow-sm"
              data-testid="nav-ios-cta"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <span>App Store</span>
            </a>

            <a
              href={ANDROID_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 bg-black text-white hover:bg-gray-800 text-xs font-semibold px-4 py-2.5 rounded-xl transition-all hover:scale-105 active:scale-95 shadow-sm"
              data-testid="nav-android-cta"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
              <span>Google Play</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:hidden">
            <a
              href={IOS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-semibold px-3 py-2 rounded-xl flex items-center gap-1 shadow-sm"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Get App</span>
            </a>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2.5 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none"
              aria-label="Toggle navigation menu"
              data-testid="nav-mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Navigation */}
      {isOpen && (
        <div className="lg:hidden bg-white/98 backdrop-blur-xl border-b border-gray-100 px-4 pt-3 pb-6 space-y-3 shadow-xl animate-in fade-in slide-in-from-top-3 duration-200">
          <div className="grid grid-cols-2 gap-2 pb-2">
            <Link
              to="/quiz/love-language"
              data-testid="nav-mobile-love-quiz"
              className={`flex items-center gap-2 p-3 rounded-2xl font-semibold text-xs border ${
                path === '/quiz/love-language'
                  ? 'bg-pink-100 text-pink-800 border-pink-200'
                  : 'bg-pink-50/80 text-pink-700 border-pink-100'
              }`}
            >
              <Heart className="w-4 h-4 fill-current text-pink-500" />
              <span>Love Quiz</span>
            </Link>

            <Link
              to="/quiz/attachment-style"
              data-testid="nav-mobile-attach-quiz"
              className={`flex items-center gap-2 p-3 rounded-2xl font-semibold text-xs border ${
                path === '/quiz/attachment-style'
                  ? 'bg-purple-100 text-purple-800 border-purple-200'
                  : 'bg-purple-50/80 text-purple-700 border-purple-100'
              }`}
            >
              <Sparkles className="w-4 h-4 text-purple-500" />
              <span>Attachment Quiz</span>
            </Link>
          </div>

          <div className="space-y-1 font-medium text-gray-800 text-base">
            <a
              href="/#features"
              onClick={(e) => handleAnchorClick(e, 'features')}
              className={getMobileLinkClasses(isFeaturesActive)}
            >
              Features
            </a>
            <a
              href="/#how-it-works"
              onClick={(e) => handleAnchorClick(e, 'how-it-works')}
              className={getMobileLinkClasses(isHowItWorksActive)}
            >
              How It Works
            </a>
            <a
              href="/#testimonials"
              onClick={(e) => handleAnchorClick(e, 'testimonials')}
              className={getMobileLinkClasses(isReviewsActive)}
            >
              Couple Reviews
            </a>
            <a
              href="/#faq"
              onClick={(e) => handleAnchorClick(e, 'faq')}
              className={getMobileLinkClasses(isFaqActive)}
            >
              FAQ
            </a>
            <Link
              to="/blog"
              onClick={() => setIsOpen(false)}
              className={getMobileLinkClasses(isBlog)}
            >
              Relationship Blog
            </Link>
          </div>

          <div className="pt-3 border-t border-gray-100 grid grid-cols-2 gap-2">
            <a
              href={IOS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 bg-black text-white text-xs font-semibold py-3 px-3 rounded-xl active:scale-95 transition-transform"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <span>iOS App</span>
            </a>
            <a
              href={ANDROID_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 bg-black text-white text-xs font-semibold py-3 px-3 rounded-xl active:scale-95 transition-transform"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
              <span>Android App</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
